import React from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn,
} from 'material-ui/Table';
import { gridHeaderColorGray } from 'theme/mainColors';
import HistoryGridMenuOptions from './comp/historyGridMenuOptions/HistoryGridMenuOptions';
import SovosPaginationFooter from 's1-ui/sovos-pagination-footer';
import LinearProgress from 'material-ui/LinearProgress';
import './importHistoryGrid.scss';

const headerLabelStyle = {
  color: gridHeaderColorGray,
  fontWeight: 400,
  fontSize: 17,
  paddingBottom: 20
};

class ImportHistoryGrid extends React.Component {
  constructor(props) {
    super(props);
    this.importHistoryGridColumns = [
      {
        title: 'Date/Time',
        dataKey: 'creationDate',
        width: 205
      },{
        title: 'Filename',
        dataKey: 'fileName'
      },{
        title: 'Period',
        dataKey: 'period',
        width: 150
      },{
        title: 'Status',
        dataKey: 'importStatusHistory',
        width: 170
      },{
        title: '',
        width: 70,
        dataKey: 'moreOptions',
        disabled: !props.showMoreOptions
      }
    ];

    this.state = {
      hoveredRow: -1,
      itemsPerPage: 10,
      page: 0,
      fileDownloadID: null,
      fileDownloadName: ''
    };
  }

  componentWillMount(){
    this.props.importHistoryActions.fetchImportHistory(0, 10);
  }

  getStartItem = () => (this.props.totalItems ? ((this.state.itemsPerPage * (this.state.page)) + 1) : 0);

  calculateEndItem = () => {
    const maximumItemsOnPage = this.state.itemsPerPage * (this.state.page + 1);
    if (maximumItemsOnPage > this.props.totalItems) {
      return this.props.totalItems;
    }
    return maximumItemsOnPage;
  };

  handlePrevPage = ev => {
    ev.preventDefault();
    this.setState(oldState => {
      if(oldState.page > 0 ) {
        this.props.onPageChange(oldState.page - 1, oldState.itemsPerPage);
        return ({
          page: oldState.page - 1
        });
      }
      else {
        return ({});
      }
    });
  };

  handleNextPage = ev => {
    ev.preventDefault();
    this.setState(oldState => {
      if((oldState.page + 1) * oldState.itemsPerPage < this.props.totalItems) {
        this.props.onPageChange(oldState.page + 1, oldState.itemsPerPage);
        return ({
          page: oldState.page + 1
        });
      }
      else {
        return ({});
      }
    });
  };

  handlePerPageChanged = ev => {
    const itemsPerPage = ev.target.value;
    this.setState({ page: 0, itemsPerPage });
    this.props.onPageChange(0, itemsPerPage);
  };

  handleDownloadFile = file => {
    this.setState({ fileDownloadID: file.id, fileDownloadName: file.name });
    this.props.importHistoryActions.getImportFile(file);
  };

  handleRowHover = hoveredRow => this.setState({ hoveredRow });

  getErrorName = name => {
    const splitName = name.split('.');
    splitName[splitName.length - 2] = `${ splitName[splitName.length - 2] }_error`;
    return splitName.join('.');
  };

  render() {
    const {
      importHistoryData,
      selectedHistoryRow,
      importHistoryStatusData,
      showPagination,
      totalItems,
      fetchingImportHistory
    } = this.props;
    return (
      <div className="import-history-grid__main-container">
        {
          fetchingImportHistory &&
          <div className="import-history-grid__over-layer">
            <LinearProgress mode="indeterminate" />
          </div>
        }
        <div className={ `${ fetchingImportHistory ? 'blur-content' : '' }` }>
          { importHistoryData.length > 0 &&
            <Table fixedHeader={ true } onRowHover={ this.handleRowHover } >
              <TableHeader displaySelectAll={ false } adjustForCheckbox={ false } style={ { border: 'none' } }>
                <TableRow style={ { border: 'none' } }>
                  { this.importHistoryGridColumns.map(({ dataKey, width, title, disabled }) => (
                    !disabled && <TableHeaderColumn
                      key={ dataKey }
                      style={ { width: width || 'auto', ...headerLabelStyle } }
                    >
                      { title }
                    </TableHeaderColumn>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody showRowHover={ true } displayRowCheckbox={ false }>
                { importHistoryData.map((history, index) => (
                  <TableRow 
                    id={ `import-history-grid-row-${index}` } 
                    key={ index } 
                    style={ { height: 40, backgroundColor: selectedHistoryRow === index ? '#f7f7f7' : '#fff' } }
                  >
                    { this.importHistoryGridColumns.map(({ dataKey, width, disabled }) => (
                      !disabled && <TableRowColumn 
                        key={ dataKey } 
                        style={ { width: width || 'auto', textOverflow: `${dataKey !== 'moreOptions' ? 'ellipsis' : 'unset'}` } } 
                      >
                        {
                          (dataKey !== 'moreOptions') ?
                            history[dataKey] :
                            this.state.hoveredRow === index &&
                          <HistoryGridMenuOptions
                            onDownloadOriginal={ () => this.handleDownloadFile({ 
                              id: history.inputFileId, name: history.fileName 
                            }) }
                            onDownloadError={ () => this.handleDownloadFile({
                              id: history.resultsFileId,
                              name: this.getErrorName(history.fileName)
                            }) }
                            downloadErrorDisabled={ history.importStatusHistory !== importHistoryStatusData[1] }
                          />
                        }
                      </TableRowColumn>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          }
          {
            importHistoryData.length > 0 && showPagination &&
            <SovosPaginationFooter
              endItem={ this.calculateEndItem() }
              itemsPerPage={ this.state.itemsPerPage }
              startItem={ this.getStartItem() }
              totalItems={ totalItems }
              onPrevClicked={ this.handlePrevPage }
              onNextClicked={ this.handleNextPage }
              onPerPageChanged={ this.handlePerPageChanged }
              itemsPerPageOptions={ [ 10, 25, 50 ] }
            />
          }
        </div>
      </div>
    );
  }
}

ImportHistoryGrid.propTypes = {
  totalItems: PropTypes.number,
  importHistoryData: PropTypes.arrayOf(PropTypes.shape({
    creationDate: PropTypes.string,
    fileName: PropTypes.string,
    period: PropTypes.string,
    importStatusHistory: PropTypes.string,
    inputFileId: PropTypes.string,
    resultsFileId: PropTypes.string,
  })).isRequired,
  importHistoryStatusData: PropTypes.arrayOf(PropTypes.string).isRequired,
  importHistoryActions: PropTypes.shape({
    fetchImportHistory: PropTypes.func,
    getImportFile: PropTypes.func
  }).isRequired,
  showMoreOptions: PropTypes.bool,
  selectedHistoryRow: PropTypes.number,
  showPagination: PropTypes.bool,
  onPageChange: PropTypes.func,
  fetchingImportHistory: PropTypes.bool
};

ImportHistoryGrid.defaultProps = {
  selectedHistoryRow: -1,
  showMoreOptions: false,
  showPagination: false,
  onPageChange: () => null,
  fetchingImportHistory: false
};

export default ImportHistoryGrid;



// WEBPACK FOOTER //
// ./src/components/importData/comp/selectImportProfilePanel/comp/importHistoryGrid/ImportHistoryGrid.js